import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { libelleMetier } from '../config/servicesCatalog';
import { useProspects } from '../hooks/useProspects';
import { trierFileAppel } from '../utils/prospectScoring';
import { listerCohortes } from '../utils/prospectStats';
import AppelDuJourList from '../components/prospection/AppelDuJourList';
import ProspectsList from '../components/prospection/ProspectsList';
import ProspectStats from '../components/prospection/ProspectStats';
import ImportProspectsModal from '../components/prospection/ImportProspectsModal';
import CreateProspectModal from '../components/prospection/CreateProspectModal';
import ProspectDetail from '../components/prospection/ProspectDetail';

/**
 * Page racine de la prospection — 4 onglets (plan §5.1).
 *
 * `prospection/:placeId` affiche la fiche ; `prospection` affiche les onglets. Les compteurs des
 * onglets sont calculés sur la liste **déjà chargée** : pas de requête `count()`, pas de seconde
 * source de vérité.
 */

const ONGLETS = [
  { id: 'file', label: "File d'appel" },
  { id: 'tous', label: 'Tous' },
  { id: 'stats', label: '📊 Stats' },
  { id: 'import', label: 'Import' }
];

const FILTRES_INITIAUX = {
  departement: '',
  metier: '',
  nonRevendiqueesSeulement: false,
  ouvertsSeulement: true,
  recherche: ''
};

/** Squelette de chargement (daisyUI) — état 1 des 4 états obligatoires. */
const Squelette = () => (
  <div className="space-y-3 animate-pulse">
    {[0, 1, 2, 3].map((ligne) => (
      <div key={ligne} className="h-20 bg-base-200 rounded-lg" />
    ))}
  </div>
);

/** Erreur explicite : `permission-denied` est LA cause n°1, le message doit le dire (§5.9). */
const BlocErreur = ({ erreur, permissionDenied, onReessayer }) => (
  <div className="alert alert-error">
    <div>
      <h3 className="font-bold">
        {permissionDenied ? 'Accès refusé par Firestore' : 'Impossible de charger les prospects'}
      </h3>
      <p className="text-sm">
        {permissionDenied
          ? 'La règle `match /prospects/{placeId}` n’est pas déployée. Lancez `firebase deploy --only firestore:rules` depuis msd_portail, puis rechargez.'
          : erreur?.message || 'Erreur inconnue.'}
      </p>
      {onReessayer && (
        <button type="button" className="btn btn-sm btn-outline mt-2" onClick={onReessayer}>
          Réessayer
        </button>
      )}
    </div>
  </div>
);

const ProspectionTabs = () => {
  const navigate = useNavigate();
  const [onglet, setOnglet] = useState('file');
  const [filtres, setFiltres] = useState(FILTRES_INITIAUX);
  const [importOuvert, setImportOuvert] = useState(false);
  const [creationOuverte, setCreationOuverte] = useState(false);

  const { prospects, tous, isLoading, isError, error, permissionDenied, refetch } =
    useProspects(filtres);

  // File d'appel : statuts retenus + tri score DESC, badge d'ouverture recalculé à chaque rendu
  const file = useMemo(
    () => trierFileAppel(prospects, { inclureFermes: !filtres.ouvertsSeulement }),
    [prospects, filtres.ouvertsSeulement]
  );

  // Départements réellement présents dans les données (pas de liste codée en dur)
  const departements = useMemo(() => {
    const codes = new Set(
      tous.map((prospect) => prospect.departmentCode).filter((code) => Boolean(code))
    );
    return [...codes].sort();
  }, [tous]);

  const cohortes = useMemo(() => listerCohortes(tous), [tous]);

  // Métiers réellement présents dans les données : le filtre suit la saisie libre du formulaire
  // manuel (aucune liste fermée à maintenir, aucun métier saisi à la main devenu infiltrable)
  const metiers = useMemo(() => {
    const codes = new Set(tous.map((prospect) => prospect.targetTrade).filter(Boolean));
    return [...codes].sort((a, b) =>
      String(libelleMetier(a)).localeCompare(String(libelleMetier(b)), 'fr')
    );
  }, [tous]);

  const changerFiltre = (cle, valeur) => setFiltres((anciens) => ({ ...anciens, [cle]: valeur }));

  if (isError) {
    return (
      <div className="max-w-7xl mx-auto p-4">
        <BlocErreur erreur={error} permissionDenied={permissionDenied} onReessayer={refetch} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4">
      <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-primary mb-2">Prospection</h1>
          <div className="text-sm breadcrumbs text-base-content/60">
            <ul>
              <li>MSD Admin</li>
              <li>Prospection</li>
            </ul>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={() => setCreationOuverte(true)}
          >
            ➕ Nouveau prospect
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setImportOuvert(true)}
          >
            ⬆️ Importer un fichier Apify
          </button>
        </div>
      </div>

      <div className="bg-base-100 rounded-lg shadow-sm p-6">
        <div role="tablist" className="tabs tabs-boxed mb-4 w-fit">
          {ONGLETS.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              className={`tab ${onglet === item.id ? 'tab-active' : ''}`}
              onClick={() => setOnglet(item.id)}
            >
              {item.label}
              {item.id === 'file' && ` (${file.length})`}
              {item.id === 'tous' && ` (${tous.length})`}
            </button>
          ))}
        </div>
        {/* FILTRES */}
        {onglet !== 'stats' && onglet !== 'import' && (
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <select
              className="select select-bordered select-sm"
              value={filtres.departement}
              onChange={(evenement) => changerFiltre('departement', evenement.target.value)}
            >
              <option value="">Tous les départements</option>
              {departements.map((code) => (
                <option key={code} value={code}>
                  {code}
                </option>
              ))}
            </select>

            <select
              className="select select-bordered select-sm"
              value={filtres.metier}
              onChange={(evenement) => changerFiltre('metier', evenement.target.value)}
            >
              <option value="">Tous les métiers</option>
              {metiers.map((code) => (
                <option key={code} value={code}>
                  {libelleMetier(code)}
                </option>
              ))}
            </select>

            <label className="label cursor-pointer gap-2 py-0">
              <input
                type="checkbox"
                className="checkbox checkbox-sm"
                checked={filtres.nonRevendiqueesSeulement}
                onChange={(evenement) =>
                  changerFiltre('nonRevendiqueesSeulement', evenement.target.checked)
                }
              />
              <span className="label-text">Non revendiquées</span>
            </label>

            <label className="label cursor-pointer gap-2 py-0">
              <input
                type="checkbox"
                className="checkbox checkbox-sm"
                checked={filtres.ouvertsSeulement}
                onChange={(evenement) => changerFiltre('ouvertsSeulement', evenement.target.checked)}
              />
              <span className="label-text">Ouverts (ou horaires inconnus)</span>
            </label>

            <input
              type="search"
              placeholder="Nom, ville, téléphone…"
              className="input input-bordered input-sm w-56"
              value={filtres.recherche}
              onChange={(evenement) => changerFiltre('recherche', evenement.target.value)}
            />
          </div>
        )}

        {isLoading ? (
          <Squelette />
        ) : (
          <>
            {onglet === 'file' && (
              <AppelDuJourList
                prospects={file}
                onOuvrir={(placeId) => navigate(`/prospection/${placeId}`)}
              />
            )}
            {onglet === 'tous' && (
              <ProspectsList
                prospects={prospects}
                onOuvrir={(placeId) => navigate(`/prospection/${placeId}`)}
              />
            )}
            {onglet === 'stats' && <ProspectStats cohortes={cohortes} tous={tous} />}
            {onglet === 'import' && (
              <div className="text-sm text-base-content/70">
                <p>
                  L’import s’ouvre dans une modale : elle affiche l’aperçu complet (fiches lues,
                  importables, rejets listés) <strong>avant</strong> toute écriture.
                </p>
                <button
                  type="button"
                  className="btn btn-primary btn-sm mt-3"
                  onClick={() => setImportOuvert(true)}
                >
                  ⬆️ Ouvrir l’import
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {importOuvert && (
        <ImportProspectsModal
          existingById={Object.fromEntries(tous.map((prospect) => [prospect.placeId, prospect]))}
          onFermer={() => setImportOuvert(false)}
        />
      )}

      {creationOuverte && (
        <CreateProspectModal
          existants={tous}
          onFermer={() => setCreationOuverte(false)}
          onCree={(placeId) => {
            setCreationOuverte(false);
            navigate(`/prospection/${placeId}`);
          }}
        />
      )}
    </div>
  );
};

const ProspectionPage = () => {
  const { placeId } = useParams();

  if (placeId) return <ProspectDetail placeId={placeId} />;

  return <ProspectionTabs />;
};

export default ProspectionPage;
